import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl, Grid, Typography, Stack,
  Divider
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import { apiClient } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const STRATEGY_COLORS = { linear: 'primary', exponential: 'warning', custom: 'secondary' };

const defaultForm = {
  scope: 'global',
  auctionRoomId: '',
  basePrice: 1,
  strategy: 'linear',
  exponentialFactor: 1.5,
  increment: 10,
  customTiers: ''
};

const AdminPacks = () => {
  const toast = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/bid-pack-configs');
      setConfigs(res.data || []);
    } catch {
      setError('Failed to load configs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const openModal = () => { setEditingConfig(null); setForm(defaultForm); setShowModal(true); };

  const openEditModal = (config) => {
    setEditingConfig(config);
    const tiers = (config.customTiers || []).map(t => {
      const parts = [t.index, t.price];
      if (typeof t.grant === 'number' && t.grant > 0) parts.push(t.grant);
      return parts.join(':');
    }).join(',');
    setForm({
      scope: config.scope || 'global',
      auctionRoomId: config.auctionRoomId || '',
      basePrice: config.basePrice ?? 1,
      strategy: config.strategy || 'linear',
      exponentialFactor: config.exponentialFactor ?? 1.5,
      increment: config.increment ?? 10,
      customTiers: tiers
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingConfig(null); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload = {
        ...form,
        basePrice: Number(form.basePrice),
        exponentialFactor: Number(form.exponentialFactor),
        increment: Number(form.increment)
      };
      if (form.strategy === 'custom') {
        const tiers = form.customTiers.split(',')
          .map(s => s.trim()).filter(Boolean)
          .map(p => {
            const parts = p.split(':');
            const i = Number(parts[0]);
            const pr = Number(parts[1]);
            const gr = parts[2] !== undefined ? Number(parts[2]) : undefined;
            const t = { index: i, price: pr };
            if (gr !== undefined && !Number.isNaN(gr)) t.grant = gr;
            return t;
          });
        for (const t of tiers) {
          if (Number.isNaN(t.index) || t.index < 1) { toast.warning('Each tier must have a valid index >= 1'); setSaving(false); return; }
          if (Number.isNaN(t.price) || t.price < 0) { toast.warning(`Tier #${t.index} must have a valid non-negative price`); setSaving(false); return; }
          if (t.index === 5 && typeof t.price === 'number' && t.price > 0) {
            if (!(typeof t.grant === 'number' && t.grant > 0)) { toast.warning('Pack #5 requires a grant. Use index:price:grant e.g. 5:10:10'); setSaving(false); return; }
          }
        }
        payload.customTiers = tiers;
      } else {
        delete payload.customTiers;
      }
      if (payload.scope !== 'auction') delete payload.auctionRoomId;
      if (editingConfig?._id) {
        await apiClient.put(`/api/bid-pack-configs/${editingConfig._id}`, payload);
      } else {
        await apiClient.post('/api/bid-pack-configs', payload);
      }
      closeModal();
      fetchConfigs();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete config?')) return;
    try { await apiClient.delete(`/api/bid-pack-configs/${id}`); fetchConfigs(); } catch { toast.error('Delete failed'); }
  };

  const customTierPreview = (() => {
    if (form.strategy !== 'custom' || !form.customTiers) return [];
    try {
      return form.customTiers.split(',').map(s => s.trim()).filter(Boolean).map(p => {
        const parts = p.split(':');
        const index = Number(parts[0]);
        const price = Number(parts[1]);
        const grant = parts[2] !== undefined ? Number(parts[2]) : undefined;
        const perBid = grant > 0 ? Math.round((price / grant) * 100) / 100 : null;
        return { index, price, grant, perBid };
      }).filter(t => !Number.isNaN(t.index));
    } catch { return []; }
  })();

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Bid Pack Pricing"
        subheader="Configure pricing strategies for bid packs"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        action={
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={openModal} sx={{ borderRadius: 2 }}>
            New Config
          </Button>
        }
      />
      {loading && <LinearProgress color="primary" />}
      <CardContent sx={{ pt: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                <TableCell>Scope</TableCell>
                <TableCell>Base Price</TableCell>
                <TableCell>Strategy</TableCell>
                <TableCell>Increment</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map(c => (
                <TableRow key={c._id} hover>
                  <TableCell>
                    <Chip label={c.scope} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{c.basePrice}</TableCell>
                  <TableCell>
                    <Chip label={c.strategy} size="small" color={STRATEGY_COLORS[c.strategy] || 'default'} />
                  </TableCell>
                  <TableCell>{c.increment || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {c.strategy === 'exponential' && `factor ${c.exponentialFactor}`}
                    {c.strategy === 'custom' && (c.customTiers || []).map(t => `${t.index}:${t.price}`).join(', ')}
                  </TableCell>
                  <TableCell>
                    <Chip label={c.active ? 'Active' : 'Inactive'} size="small" color={c.active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {new Date(c.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="warning" onClick={() => openEditModal(c)} title="Edit">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(c._id)} title="Delete">
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    <Stack alignItems="center" spacing={1}>
                      <InventoryOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      <Typography variant="body2">No configs found</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryOutlinedIcon />
          {editingConfig ? 'Edit Bid Pack Config' : 'New Bid Pack Config'}
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Scope</InputLabel>
                <Select name="scope" value={form.scope} label="Scope" onChange={handleChange}>
                  <MenuItem value="global">Global</MenuItem>
                  <MenuItem value="auction">Auction Specific</MenuItem>
                </Select>
              </FormControl>

              {form.scope === 'auction' && (
                <TextField fullWidth size="small" label="Auction Room ID" name="auctionRoomId" value={form.auctionRoomId} onChange={handleChange} required />
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Base Price" name="basePrice" value={form.basePrice} onChange={handleChange} inputProps={{ min: 0, step: 0.01 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Strategy</InputLabel>
                    <Select name="strategy" value={form.strategy} label="Strategy" onChange={handleChange}>
                      <MenuItem value="linear">Linear</MenuItem>
                      <MenuItem value="exponential">Exponential</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {form.strategy === 'exponential' && (
                <TextField fullWidth size="small" type="number" label="Exponential Factor" name="exponentialFactor" value={form.exponentialFactor} onChange={handleChange} inputProps={{ min: 1, step: 0.1 }} />
              )}

              {form.strategy === 'custom' && (
                <Stack spacing={1}>
                  <TextField
                    fullWidth size="small"
                    label="Custom Tiers (index:price[:grant], comma separated)"
                    name="customTiers"
                    value={form.customTiers}
                    onChange={handleChange}
                    placeholder="1:1:10,2:3:8,3:6:12,5:10:10"
                    helperText="Pack #5 requires a grant value for the unlimited tier."
                  />
                  {customTierPreview.length > 0 && (
                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                      <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>Preview:</Typography>
                      {customTierPreview.map(t => (
                        <Typography key={t.index} variant="caption" display="block">
                          Tier #{t.index}: price {t.price}
                          {typeof t.grant === 'number' ? `, grant ${t.grant}` : ''}
                          {t.perBid !== null ? `, per-bid ${t.perBid}` : ''}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              )}

              <TextField
                fullWidth size="small" type="number" label="Bids per Pack (increment)"
                name="increment" value={form.increment} onChange={handleChange}
                inputProps={{ min: 1, step: 1 }}
                helperText="Bids granted per purchased pack (applies to packs 1–4; pack #5 is unlimited)."
              />
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
};

export default AdminPacks;
